"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = exports.createSortText = exports.createSortNumeric = exports.createSortDate = exports.TEST = exports.PARTS = void 0;
// helpers
const isDefined = v => v !== undefined && v !== null;
const isDate = v => isDefined(v) && !Number.isNaN(new Date(v).getTime());
const toString = v => isDefined(v) && v.toString ? v.toString() : v;
const toUpperCase = v => typeof v === 'string' ? v.toUpperCase() : v;
const isNum = v => {
  try {
    return isDefined(v) && !Number.isNaN(Number.parseFloat(v));
  } catch (e) {
    return false;
  }
};
const toNumber = v => isNum(v) ? Number.parseFloat(v) : v;
const toDate = v => isDate(v) ? new Date(v) : v;

// CORE
const sortNullAndUndefinedNullFirst = (v1, v2) => {
  if (v1 === null && v2 === undefined) {
    return -1; // return null first
  }
  if (v2 === null && v1 === undefined) {
    return 1; // return null first
  }
  return 0;
};
const dumbSortAsc = (v1, v2) => {
  if (v1 < v2) {
    return -1;
  }
  if (v2 < v1) {
    return 1;
  }
  return 0;
};
const dumbSortDesc = (v1, v2) => dumbSortAsc(v1, v2) * -1;

// PURE

// PROTECT
const protectDefinedAsc = function (_v1, _v2) {
  let {
    getValue = v => v,
    f = () => 0
  } = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  const v1 = getValue(_v1);
  const v2 = getValue(_v2);
  if (isDefined(v1) && isDefined(v2)) {
    return f(v1, v2);
  }
  if (!isDefined(v1) && !isDefined(v2)) {
    return sortNullAndUndefinedNullFirst(v1, v2); // since sort on flat values always return undefined last, we will always return undefined after null
  }
  if (isDefined(v1)) {
    return -1; // if arrayitem === undefined, protectDefinedAsc will not be called and undefined values will always be last
  }
  if (isDefined(v2)) {
    return 1; // if arrayitem === undefined, protectDefinedAsc will not be called and undefined values will always be last
  }
  return 0; // if arrayitem === undefined, protectDefinedAsc will not be called and undefined values will always be last
};
const protectDefinedDesc = function (_v1, _v2) {
  let {
    invalidLast = false,
    getValue = v => v,
    f = () => 0
  } = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  const v1 = getValue(_v1);
  const v2 = getValue(_v2);
  if (isDefined(v1) && isDefined(v2)) {
    return f(v1, v2);
  }
  if (!isDefined(v1) && !isDefined(v2)) {
    return sortNullAndUndefinedNullFirst(v1, v2); // since sort on flat values always return undefined last, we will always return undefined after null
  }
  if (isDefined(v1)) {
    return invalidLast ? -1 : 1; // if arrayitem === undefined, protectDefinedDesc will not be called and undefined values will always be lastast
  }
  if (isDefined(v2)) {
    return invalidLast ? 1 : -1; // if arrayitem === undefined, protectDefinedDesc will not be called and undefined values will always be last
  }
  return 0; // if arrayitem === undefined, protectDefinedDesc will not be called and undefined values will always be last
};

// EXPORTS
const createSortText = function () {
  let direction = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'ASC';
  let {
    getValue = i => i,
    caseSensitive = true,
    invalidLast = false
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return (i1, i2) => {
    let v1 = toString(getValue(i1));
    let v2 = toString(getValue(i2));
    if (!caseSensitive) {
      v1 = toUpperCase(v1);
      v2 = toUpperCase(v2);
    }
    let protectDefined = protectDefinedAsc;
    let dumbSort = dumbSortAsc;
    if (direction === 'DESC') {
      protectDefined = protectDefinedDesc;
      dumbSort = dumbSortDesc;
    }
    return protectDefined(v1, v2, {
      invalidLast,
      f: (_v1, _v2) => dumbSort(_v1, _v2)
    });
  };
};
exports.createSortText = createSortText;
const createSortNumeric = function () {
  let direction = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'ASC';
  let {
    getValue = i => i,
    caseSensitive = false,
    invalidLast = false
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return (i1, i2) => {
    const v1 = toNumber(getValue(i1));
    const v2 = toNumber(getValue(i2));
    let protectDefined = protectDefinedAsc;
    let dumbSort = dumbSortAsc;
    if (direction === 'DESC') {
      protectDefined = protectDefinedDesc;
      dumbSort = dumbSortDesc;
    }
    if (isNum(v1) && isNum(v2)) {
      return protectDefined(v1, v2, {
        invalidLast,
        f: (_v1, _v2) => dumbSort(_v1, _v2)
      });
    }
    if (isNum(v1)) {
      return direction === 'DESC' ? 1 : -1;
    }
    if (isNum(v2)) {
      return direction === 'DESC' ? -1 : 1;
    }
    return createSortText(direction, {
      getValue,
      caseSensitive
    })(i1, i2);
  };
};
exports.createSortNumeric = createSortNumeric;
const createSortDate = function () {
  let direction = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'ASC';
  let {
    getValue = i => i,
    caseSensitive,
    invalidLast = false
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return (i1, i2) => {
    const v1 = toDate(getValue(i1));
    const v2 = toDate(getValue(i2));
    let protectDefined = protectDefinedAsc;
    let dumbSort = dumbSortAsc;
    if (direction === 'DESC') {
      protectDefined = protectDefinedDesc;
      dumbSort = dumbSortDesc;
    }
    if (isDate(v1) && isDate(v2)) {
      return protectDefined(v1, v2, {
        f: (_v1, _v2) => dumbSort(_v1, _v2)
      });
    }
    if (isDate(v1)) {
      return direction === 'DESC' && !invalidLast ? 1 : -1;
    }
    if (isDate(v2)) {
      return direction === 'DESC' && !invalidLast ? -1 : 1;
    }
    return createSortText(direction, {
      getValue,
      caseSensitive,
      invalidLast
    })(i1, i2);
  };
};
exports.createSortDate = createSortDate;
const TEST = {
  dumbSortAsc,
  dumbSortDesc,
  protectDefinedAsc,
  protectDefinedDesc,
  createSortText,
  createSortNumeric,
  createSortDate
};

// parts can be used to create your own customized highlevel sorts
exports.TEST = TEST;
const PARTS = {
  dumbSortAsc,
  dumbSortDesc,
  protectDefinedAsc,
  protectDefinedDesc
};
exports.PARTS = PARTS;
var _default = {
  TEST,
  PARTS,
  createSortText,
  createSortNumeric,
  createSortDate
};
exports.default = _default;